import { useEffect, useMemo, useRef } from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import { resolveImageSrcsInText } from "./types";
import { attachImageErrorFallback } from "./image-fallback";
import { applyInlineMarkdown } from "./problem-text";
import { preprocessConfig } from "./survey-config";
import { ProblemImageRefs, type DiagramState } from "./ProblemImages";

declare global {
  interface Window {
    MathJax?: {
      typesetPromise?: (nodes?: HTMLElement[]) => Promise<void>;
      typeset?: (nodes?: HTMLElement[]) => void;
      /** Installed by MathJax v3 once loaded; absent until then. */
      startup?: { promise?: Promise<unknown> };
    };
  }
}

/**
 * Resolves once MathJax can typeset. Never rejects.
 *
 * One shared promise, so N renderers do not start N polls. The timer is bound to
 * the page rather than any component: it self-clears as soon as MathJax appears
 * (sub-second in practice) or after the deadline, and MathJax loads once per
 * page, so at most one 50ms timer ever runs. It is not cancelled on unmount.
 */
let mathJaxLoad: Promise<void> | null = null;

/** Either shape is enough to typeset; `startup` only exists post-load. */
function mathJaxUsable(): boolean {
  const mj = window.MathJax;
  return Boolean(mj?.startup?.promise || mj?.typesetPromise || mj?.typeset);
}

function mathJaxLoaded(): Promise<void> {
  if (mathJaxUsable()) {
    // Swallow a rejected startup promise: callers only need "stop waiting".
    return Promise.resolve(window.MathJax?.startup?.promise).then(
      () => undefined,
      () => undefined,
    );
  }
  mathJaxLoad ??= new Promise<void>((resolve) => {
    const deadline = Date.now() + 15_000;
    const timer = setInterval(() => {
      const usable = mathJaxUsable();
      if (usable || Date.now() > deadline) {
        clearInterval(timer);
        // Only cleared when MathJax actually arrived. Clearing on the timeout
        // path too meant a blocked CDN restarted a fresh 15s poll on every
        // subsequent question render, forever, uncancelled on unmount.
        if (usable) mathJaxLoad = null;
        resolve();
      }
    }, 50);
  });
  return mathJaxLoad;
}

/**
 * Typeset once MathJax is loaded. Reads `window.MathJax` rather than taking the
 * context promise, so SurveyJS callbacks — created once per config — can call it
 * without holding a ref to the current render.
 *
 * Called from onAfterRenderQuestion, which fires on every render including a
 * re-mount. Waiting only on first load left a tab switch showing raw `$…$`.
 */
async function typesetWhenReady(el: HTMLElement) {
  try {
    await mathJaxLoaded();
    const mj = window.MathJax;
    if (mj?.typesetPromise) await mj.typesetPromise([el]);
    else mj?.typeset?.([el]);
  } catch {
    // A typeset failure must not take the dialog down.
  }
}

export default function SurveyRenderer({
  config,
  images,
  diagrams,
}: {
  config: Record<string, unknown>;
  /** Used to repair `<img>` tags whose src the scraper destroyed. */
  images?: unknown;
  diagrams?: DiagramState;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { config: cleanedConfig, leftoverRefs } = useMemo(
    () => preprocessConfig(config, images),
    [config, images],
  );

  // Rebuilding the Model re-runs SurveyJS's whole question tree plus the
  // LaTeX/MathJax pass, so keep it tied to the config rather than every render.
  const surveyModel = useMemo(() => {
    const model = new Model({
      elements: [cleanedConfig],
      showNavigationButtons: false,
      mode: "display",
    });

    model.onTextMarkdown.add((_survey, options) => {
      options.html = resolveImageSrcsInText(applyInlineMarkdown(options.text));
    });

    model.onAfterRenderQuestion.add((_survey, options) => {
      const el = options.htmlElement as HTMLElement;
      void typesetWhenReady(el);
    });

    return model;
  }, [cleanedConfig]);

  // Catch-all: SurveyJS renders some content outside onAfterRenderQuestion.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await mathJaxLoaded();
      const el = containerRef.current;
      if (cancelled || !el) return;
      await typesetWhenReady(el);
    })();
    return () => {
      cancelled = true;
    };
  }, [config]);

  // SurveyJS injects into its own DOM outside React, so image errors need a
  // plain DOM listener to get the same fallback as the plain path.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    return attachImageErrorFallback(root);
  }, [config]);

  return (
    // qb-survey scopes the option-appearance overrides in question-bank.css.
    <div ref={containerRef} className="qb-survey">
      {/* Owned here, not by callers: only this component knows which figures
          landed in the config, so this is the one place that can guarantee each
          one renders exactly once. */}
      <ProblemImageRefs refs={leftoverRefs} diagrams={diagrams} />
      <Survey model={surveyModel} />
    </div>
  );
}
